/**
 * Build agentic v2 format from legacy appreciation data
 * This ensures the Discovery Panel ALWAYS has data, even from old cached responses
 */

function extractConceptName(text) {
  if (!text || typeof text !== 'string') return 'Art Technique';
  
  const lowerText = text.toLowerCase();
  
  // "The artist used X here" → extract X as concept name
  const usedMatch = text.match(/\b(?:the artist|artist)\s+used\s+([a-z\s]+?)(?:\s+here|\s+in this|\.|,)/i);
  if (usedMatch && usedMatch[1]) {
    const phrase = usedMatch[1].trim();
    if (phrase.length >= 3 && phrase.length <= 30) {
      const name = phrase.replace(/\b\w/g, c => c.toUpperCase());
      return name;
    }
  }

  // Common art concepts to look for
  const conceptPatterns = [
    { pattern: /\b(bold colors?|bright colors?|strong colors?)\b/i, name: 'Bold Colors' },
    { pattern: /\b(texture|textured|textures)\b/i, name: 'Texture' },
    { pattern: /\b(balance|balanced|balancing)\b/i, name: 'Balance' },
    { pattern: /\b(color contrast|contrast|contrasting)\b/i, name: 'Color Contrast' },
    { pattern: /\b(light and shadow|light|shadow|shadows|highlights)\b/i, name: 'Light and Shadow' },
    { pattern: /\b(shape|shapes|shape variety)\b/i, name: 'Shape Variety' },
    { pattern: /\b(line|lines|line direction)\b/i, name: 'Line Direction' },
    { pattern: /\b(composition|composing)\b/i, name: 'Composition' },
    { pattern: /\b(perspective|perspectives)\b/i, name: 'Perspective' },
    { pattern: /\b(overlap|overlapping)\b/i, name: 'Overlap' },
    { pattern: /\b(pattern|patterns)\b/i, name: 'Pattern' },
    { pattern: /\b(reflection|reflections)\b/i, name: 'Reflections' },
    { pattern: /\b(color mixing|color blend)\b/i, name: 'Color Mixing' },
    { pattern: /\b(warm colors|cool colors)\b/i, name: 'Color Temperature' },
    { pattern: /\b(size|sizes|size variation)\b/i, name: 'Size Variation' },
    { pattern: /\b(vertical|horizontal|diagonal)\b/i, name: 'Line Direction' }
  ];

  for (const { pattern, name } of conceptPatterns) {
    if (pattern.test(text)) {
      return name;
    }
  }

  // Fallback: try to extract from first sentence
  const firstSentence = text.split(/[.?!]/)[0];
  if (firstSentence.length > 0 && firstSentence.length < 50) {
    return firstSentence.trim().charAt(0).toUpperCase() + firstSentence.trim().slice(1).toLowerCase();
  }

  return 'Art Technique';
}

function generateConceptSpecificSteps(text, elementIndex) {
  const conceptName = extractConceptName(text);
  const lowerConcept = conceptName.toLowerCase();
  
  const positionHints = [
    'Focus on the front part of the scene.',
    'Focus on the middle part of the scene.',
    'Focus on the far or back part of the scene.',
    'Try using this across the whole picture.'
  ];
  
  const positionHint = positionHints[elementIndex] || positionHints[3];
  
  // Concept-specific steps
  if (lowerConcept.includes('texture')) {
    return [
      'Look closely at how the surface feels rough or smooth in this area.',
      'Use your pencil to make small marks that create texture.',
      positionHint,
      'Try making different textures: rough, smooth, bumpy, or scratchy.'
    ];
  } else if (lowerConcept.includes('balance')) {
    return [
      'Notice how things are arranged on each side of this area.',
      'Try drawing something similar with balance in mind.',
      positionHint,
      'See if your drawing feels balanced when you look at it.'
    ];
  } else if (lowerConcept.includes('color')) {
    return [
      'Look at the colors in this area of the artwork.',
      'Try using similar colors in your own drawing.',
      positionHint,
      'See how colors can make different parts stand out.'
    ];
  } else if (lowerConcept.includes('light') || lowerConcept.includes('shadow')) {
    return [
      'Notice where the light and shadows are in this area.',
      'Try adding light and shadow to your drawing.',
      positionHint,
      'See how light and shadow make things look three-dimensional.'
    ];
  } else {
    return [
      'Look closely at this part of the artwork.',
      'Try drawing something similar in your own way.',
      positionHint,
      'See what you notice when you look carefully.'
    ];
  }
}

/**
 * Build agentic v2 format from legacy appreciation data
 * This ensures 100% reliability - Discovery Panel will ALWAYS have data
 */
export function buildAgenticV2FromLegacy(payload, artwork) {
  // If payload already has agenticFormat, use it
  if (payload?.agenticFormat?.discover?.elements) {
    return payload.agenticFormat;
  }
  if (payload?.agenticAnalysis?.discover?.elements) {
    return payload.agenticAnalysis;
  }

  // Extract appreciation points
  const appreciationPoints = Array.isArray(payload?.appreciation)
    ? payload.appreciation.slice(0, 4)
    : [];

  // If no appreciation points, try to extract from points array
  if (appreciationPoints.length === 0 && Array.isArray(payload?.points)) {
    appreciationPoints.push(...payload.points.slice(0, 4).map(p => 
      typeof p === 'string' ? p : (p?.text || p?.description || JSON.stringify(p))
    ));
  }

  // CRITICAL: If no appreciation points, create minimal valid structure
  // This ensures the function NEVER returns null, guaranteeing 100% reliability
  if (appreciationPoints.length === 0) {
    const artworkType = artwork?.typeOfArtwork || 'Not Specified';
    const roleMap = {
      'Portrait': 'Portrait Explorer',
      'Self-Portrait': 'Self-Portrait Detective',
      'Architectural': 'Building Builder',
      'Still Life': 'Object Observer',
      'Seascape': 'Ocean Explorer',
      'Abstract': 'Shape Seeker',
      'Calligraphy': 'Letter Artist',
      'Sculpture': '3D Detective',
      'Floral': 'Flower Finder',
      'Animal/Birds': 'Animal Artist',
      'Miniature': 'Miniature Detective',
      'Sacred': 'Symbol Explorer',
      'Study/Sketch': 'Sketch Explorer',
      'Genre Scene': 'Scene Explorer',
      'Interior': 'Building Builder'
    };
    const roleTitle = roleMap[artworkType] || 'Art Explorer';
    
    // Last resort: create minimal structure - ALWAYS returns valid structure
    return {
      meta: {
        artworkType,
        confidence: 0.5,
        ageBand: '5-8',
        version: 'v2'
      },
      discover: {
        roleTitle,
        elements: [
          {
            id: 'el_1',
            label: 'Artwork Details',
            whereToLook: 'Look at the whole picture',
            unlockReward: 'Art Explorer!',
            concepts: [{
              id: 'c_1_1',
              name: 'Art Technique',
              oneLine: 'Explore the artwork to discover its secrets.',
              details: {
                what: `Look closely at "${artwork?.title || 'this artwork'}". Notice the colors, shapes, and how everything is arranged. The artist might have used special techniques to make it interesting.`,
                impact: 'Exploring artwork helps you see details you might miss at first glance.',
                tryIt: {
                  materials: ['pencil', 'paper'],
                  steps: [
                    'Look at the artwork carefully.',
                    'Try drawing something similar.',
                    'See what you notice.'
                  ]
                }
              }
            }],
            microChallenge: 'Try drawing your own version of this artwork.'
          }
        ]
      }
    };
  }

  const artworkType = artwork?.typeOfArtwork || 'Not Specified';

  // Role title mapping
  const roleMap = {
    'Portrait': 'Portrait Explorer',
    'Self-Portrait': 'Self-Portrait Detective',
    'Architectural': 'Building Builder',
    'Still Life': 'Object Observer',
    'Seascape': 'Ocean Explorer',
    'Abstract': 'Shape Seeker',
    'Calligraphy': 'Letter Artist',
    'Sculpture': '3D Detective',
    'Floral': 'Flower Finder',
    'Animal/Birds': 'Animal Artist',
    'Miniature': 'Miniature Detective',
    'Sacred': 'Symbol Explorer',
    'Study/Sketch': 'Sketch Explorer',
    'Genre Scene': 'Scene Explorer',
    'Interior': 'Building Builder'
  };

  const roleTitle = roleMap[artworkType] || 'Art Explorer';

  const positionLabels = [
    { label: 'Front Area', where: 'near the front of the picture' },
    { label: 'Middle Area', where: 'in the middle of the picture' },
    { label: 'Back Area', where: 'further back in the picture' },
    { label: 'Whole Picture', where: 'across the whole picture' }
  ];

  const fallbackRewards = [
    'Art Explorer!',
    'Shape Detective!',
    'Color Adventurer!',
    'Story Finder!'
  ];

  const elements = appreciationPoints.map((text, idx) => {
    const safeText = typeof text === 'string' ? text : JSON.stringify(text);
    const pos = positionLabels[idx] || positionLabels[positionLabels.length - 1];
    const reward = fallbackRewards[idx] || 'Art Explorer!';

    // Basic split to get a shorter teaser line
    const oneLine =
      safeText.split(/[.?!]/)[0].trim().slice(0, 120) ||
      'A simple art trick the artist might have used here.';

    // Ensure detailed what section (40-250 words)
    let what = `${safeText} `;
    what += 'The artist might have used this idea to make this part of the picture feel special. ';
    what += 'Look for the most important objects in this area and notice how their shapes, edges, and colors are drawn. ';
    what += 'Notice how lines, color changes, and light or shadow help connect those objects and gently guide your eyes around this part of the artwork.';
    
    // Ensure detailed impact section (30-150 words)
    let impact = 'This trick helps this area stand out and feel more interesting. ';
    impact += 'It can guide your eyes, make the story clearer, and help the whole picture feel more alive for the viewer. ';

    return {
      id: `el_${idx + 1}`,
      label: pos.label,
      whereToLook: pos.where,
      unlockReward: reward,
      concepts: [
        {
          id: `c_${idx + 1}_1`,
          name: extractConceptName(safeText) || 'Art Technique',
          oneLine,
          details: {
            what,
            impact,
            tryIt: {
              materials: ['pencil', 'paper'],
              steps: generateConceptSpecificSteps(safeText, idx)
            }
          }
        }
      ],
      microChallenge:
        'Pick your favourite area from this artwork and try recreating its feeling in your own small drawing.'
    };
  });

  return {
    meta: {
      artworkType,
      confidence: 0.6,
      ageBand: '5-8',
      version: 'v2'
    },
    discover: {
      roleTitle,
      elements
    }
  };
}

/**
 * Ensure agenticFormat is always present in appreciation result
 * This is the 100% reliable fallback function
 */
export function ensureAgenticFormat(result, artwork) {
  if (!result) return null;
  
  // If already has valid agenticFormat, return as-is
  if (result.agenticFormat?.discover?.elements?.length > 0) {
    return result;
  }
  if (result.agenticAnalysis?.discover?.elements?.length > 0) {
    return result;
  }

  // Build from legacy data
  const agenticFormat = buildAgenticV2FromLegacy(result, artwork);
  
  return {
    ...result,
    agenticFormat,
    agenticAnalysis: agenticFormat
  };
}
